import { useState } from 'react';
import { Button, Input } from '@mlm/ui';
import type { AssistantIntakeQuestionDto } from '../../lib/assistant-api';

/**
 * The persona's "tell me about you" card. Two modes:
 *  - `first-run`: shown on an empty thread while questions are unanswered —
 *    Save / Skip (skip persists server-side so it never nags again).
 *  - `edit`: opened from the thread header's "About you" button — prefilled,
 *    Save / Cancel.
 * Only CHANGED answers are submitted; clearing a field submits '' (the API
 * deletes that answer). Everything is optional — partial saves are fine.
 */
export function IntakeCard({
  icon,
  personaName,
  questions,
  saving,
  mode,
  onSave,
  onSkip,
  onCancel,
}: {
  icon: string;
  personaName: string;
  questions: AssistantIntakeQuestionDto[];
  saving: boolean;
  mode: 'first-run' | 'edit';
  onSave: (answers: Record<string, string>) => void;
  onSkip?: () => void;
  onCancel?: () => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, string>>(() =>
    Object.fromEntries(questions.map((q) => [q.key, q.answer ?? ''])),
  );
  const changes: Record<string, string> = {};
  for (const q of questions) {
    const draft = (drafts[q.key] ?? '').trim();
    if (draft !== (q.answer ?? '')) changes[q.key] = draft;
  }
  const hasChanges = Object.keys(changes).length > 0;

  return (
    <div className="mx-auto w-full max-w-md rounded-xl border bg-muted/30 p-4">
      <div className="flex items-start gap-3">
        <span className="text-2xl" aria-hidden>
          {icon}
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            {mode === 'first-run' ? `Help your ${personaName} know you` : 'About you'}
          </p>
          <p className="text-xs text-muted-foreground">
            {mode === 'first-run'
              ? 'Answers are saved so future chats fit you — not a generic member. All optional.'
              : `What your ${personaName} remembers about you. Clear a field to forget it.`}
          </p>
        </div>
      </div>

      <div className="mt-3 space-y-3">
        {questions.map((q) => (
          <label key={q.key} className="block space-y-1">
            <span className="text-xs font-medium">{q.question}</span>
            <Input
              value={drafts[q.key] ?? ''}
              placeholder={q.placeholder}
              maxLength={500}
              disabled={saving}
              onChange={(e) => setDrafts((d) => ({ ...d, [q.key]: e.target.value }))}
            />
          </label>
        ))}
      </div>

      <div className="mt-4 flex items-center gap-2">
        <Button size="sm" disabled={saving || !hasChanges} onClick={() => onSave(changes)}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
        {mode === 'first-run' && onSkip ? (
          <Button variant="ghost" size="sm" disabled={saving} onClick={onSkip}>
            Don&rsquo;t ask again
          </Button>
        ) : null}
        {mode === 'edit' && onCancel ? (
          <Button variant="ghost" size="sm" disabled={saving} onClick={onCancel}>
            Cancel
          </Button>
        ) : null}
      </div>
    </div>
  );
}
